/**
 * Signed in, nowhere to be.
 *
 * This screen replaced a 403 that said "ask whoever set yours up for an invite
 * link" — advice with no button under it, shown most often to the one person
 * who set the thing up and had nobody to ask. A dead end that names your
 * options without offering any of them is worse than an error, because it
 * reads as your fault.
 *
 * So: whatever the server says you can actually do, and nothing you cannot.
 * If a household is standing empty — the one `docker compose up` seeds — that
 * is the first offer, because it already holds your categories and your
 * entries. Starting fresh is always available underneath.
 *
 * Joining someone else's household is not offered, and the text says why in
 * one line rather than leaving a hole. It needs an invite, and being able to
 * ask your way into a stranger's household is what invites exist to prevent.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { authApi, messageOf } from './api.js';

interface Options {
  unclaimed: { id: string; name: string } | null;
  suggestedName: string;
}

export function NoHousehold({ onReady, onSignOut }: { onReady: () => void; onSignOut: () => void }) {
  const [options, setOptions] = useState<Options | null>(null);
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Shown only once there is nothing to claim, or the person asks for it. */
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authApi
      .onboarding()
      .then((data) => {
        if (cancelled) return;
        setOptions(data);
        setName(data.suggestedName);
        // With nothing to claim there is only one path, so do not make them
        // press a button to reveal the only thing they can do.
        setCreating(!data.unclaimed);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(messageOf(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function claim() {
    setBusy(true);
    setError(null);
    try {
      await authApi.claimHousehold();
      onReady();
    } catch (e: unknown) {
      setError(messageOf(e));
      setBusy(false);
    }
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authApi.createHousehold({ name: name.trim(), currency });
      onReady();
    } catch (e: unknown) {
      setError(messageOf(e));
      setBusy(false);
    }
  }

  if (!options && !error) {
    return (
      <main className="boot">
        <h1>Excent</h1>
        <p className="hint">Loading…</p>
      </main>
    );
  }

  return (
    <main className="signin">
      <div className="signin-card">
        <h1>One more step</h1>
        <p className="hint">
          Your account exists, but it is not part of a household yet — and a household is what
          everything else hangs off: the categories, the budget, the entries.
        </p>

        {error && <p className="error">{error}</p>}

        {options?.unclaimed && !creating && (
          <>
            <div className="claim-offer">
              <p>
                <strong>{options.unclaimed.name}</strong> is here and has no owner. Take it and
                whatever is already in it — categories, entries, the lot — is yours.
              </p>
              <button
                type="button"
                className="primary signin-submit"
                disabled={busy}
                onClick={() => void claim()}
              >
                {busy ? 'One moment…' : `Take ${options.unclaimed.name}`}
              </button>
            </div>
            <p className="signin-or" aria-hidden="true">
              <span>or</span>
            </p>
            <p className="signin-switch">
              <button type="button" className="linkish" onClick={() => setCreating(true)}>
                Start a new household instead
              </button>
            </p>
          </>
        )}

        {creating && (
          <form onSubmit={(e) => void create(e)}>
            <label className="signin-field">
              <span className="field-label">Household name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Our household"
                autoFocus
                required
              />
            </label>

            <label className="signin-field">
              <span className="field-label">Currency</span>
              {/* Three letters, typed. The full picker lives in Settings and
                  needs the household to exist first; asking someone to scroll
                  300 options before they have a household is a worse first
                  minute than typing three characters. It is changeable later,
                  and changing it relabels rather than converts (§3.5j). */}
              <input
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
                placeholder="EUR"
                maxLength={3}
                pattern="[A-Za-z]{3}"
                required
              />
              <span className="hint">You can change this later in Settings.</span>
            </label>

            <button
              type="submit"
              className="primary signin-submit"
              disabled={busy || !name.trim() || currency.length !== 3}
            >
              {busy ? 'One moment…' : 'Create household'}
            </button>
          </form>
        )}

        <p className="hint signin-switch">
          Meant to join someone else&rsquo;s household? That takes an invite link from someone
          already in it — there is no way to ask from out here, on purpose.
        </p>

        <p className="signin-switch">
          <button type="button" className="linkish" onClick={onSignOut}>
            Sign out
          </button>
        </p>
      </div>
    </main>
  );
}
